class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.isUpcoming = this.dataset.upcoming === 'true';
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.variantRadios = this.querySelector('variant-radios');
    this.productBody = this.closest('product-body');
    this.productImage = this.productBody?.querySelector('product-image img');
    this.defaultProductImageSrc = this.productImage?.src;
    this.purchaseLimitMessage = this.form.querySelector('#ProductFormPurchaseLimitMessage');

    if (this.variantRadios) {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map(fieldset => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value;
      });

      this.variantRadios.addEventListener('change', this.onVariantChange.bind(this));
    }

    this.variantInput = this.querySelector('input[name="id"]');
    this.price = this.querySelector('price');
    this.variantTitle = this.querySelector('variant-title');
    this.addButton = this.querySelector('[name="add"]');
    this.policy = JSON.parse(this.querySelector('[data-purchase-policy]').textContent);
    this.currentVariant = this.getVariantData().find(variant => String(variant.id) === this.variantInput.value);
    this.errorMessage = document.createElement('p');
    this.errorMessage.className = 'product-form__purchase-limit-reached hidden';
    this.errorMessage.setAttribute('role', 'alert');
    this.errorMessage.hidden = true;
    this.errorMessage.classList.add('hidden');
    this.form.appendChild(this.errorMessage);
    this.retryButton = document.createElement('button');
    this.retryButton.type = 'button';
    this.retryButton.className = 'typography-small text-link hidden';
    this.retryButton.textContent = this.policy.checkCart;
    this.retryButton.addEventListener('click', async () => {
      this.retryButton.disabled = true;
      await this.toggleAddButton();
      this.retryButton.disabled = false;
    });
    this.form.appendChild(this.retryButton);
    this.checkVersion = 0;
    this.submitting = false;
    this.toggleAddButton();
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.errorMessage.hidden = true;
    this.errorMessage.classList.add('hidden');
    this.toggleAddButton();
    if (!this.currentVariant) return;
    this.updateVariantInput();
    this.updateImage();
    this.updatePrice();
    this.updateVariantTitle();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value;
    });
  }

  getVariantData() {
    this.variantData = this.variantData || (this.variantRadios ? JSON.parse(this.variantRadios.querySelector('[type="application/json"]').textContent) : this.policy.variants);
    return this.variantData;
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find(variant => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  createAddButton() {
    const addToCartButton = document.createElement('button');
    addToCartButton.type = 'submit';
    addToCartButton.name = 'add';
    addToCartButton.className = 'typography-button typography-primary product-form__button';

    const buttonLabel = document.createElement('span');
    buttonLabel.className = 'product-form__button-label';
    buttonLabel.textContent = window.variantStrings.addToCart;
    addToCartButton.appendChild(buttonLabel);

    const loadingElement = document.createElement('span');
    loadingElement.className = 'product-form__button-loading rotating';
    const loadingSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    loadingSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    loadingSvg.setAttribute('class', 'icon');
    loadingSvg.setAttribute('fill', 'currentColor');
    loadingSvg.setAttribute('viewBox', '0 0 28 28');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M14 5a8.938 8.938 0 0 1 6.344 2.641l1.86-1.86.796 5.506-5.578-.724 1.867-1.867A7.46 7.46 0 0 0 14 6.5c-4.136 0-7.5 3.364-7.5 7.5s3.364 7.5 7.5 7.5c2.904 0 5.422-1.662 6.668-4.082l1.287.784A8.994 8.994 0 0 1 14 23a9 9 0 0 1 0-18Z');
    loadingSvg.appendChild(path);
    loadingElement.appendChild(loadingSvg);
    addToCartButton.appendChild(loadingElement);
    return addToCartButton;
  }

  ensureAddButton() {
    if (!this.addButton) {
      this.addButton = this.createAddButton();
      this.form.appendChild(this.addButton);
    }
    return this.addButton;
  }

  enableAddButton() {
    if (this.isUpcoming || !this.currentVariant?.available || this.submitting) return;
    const button = this.ensureAddButton();
    button.disabled = false;
    button.querySelector('.product-form__button-label').textContent = window.variantStrings.addToCart;
  }

  purchaseState(cart, quantity = 1) {
    const variant = this.currentVariant;
    const policy = this.policy.variants.find(entry => String(entry.id) === String(variant?.id));
    if (this.isUpcoming || !variant?.available || !policy?.available) return 'unavailable';
    if (!Array.isArray(cart.items) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error(this.policy.error);
    const matching = item => this.policy.scope === 'product'
      ? String(item.product_id) === String(this.policy.productId)
      : String(item.variant_id) === String(variant.id);
    const count = predicate => cart.items.filter(predicate).reduce((total, item) => {
      if (!Number.isSafeInteger(item.quantity) || item.quantity < 0) throw new Error(this.policy.error);
      return total + item.quantity;
    }, 0);
    if (policy.capEnabled !== false) {
      if (!Number.isSafeInteger(policy.cap) || policy.cap < 1 || !Number.isSafeInteger(policy.purchased) || policy.purchased < 0) return 'limit';
      if (policy.purchased + count(matching) + quantity > policy.cap) return 'limit';
    }
    if (policy.stock !== null) {
      if (!Number.isSafeInteger(policy.stock) || policy.stock < 0) throw new Error(this.policy.error);
      if (count(item => String(item.variant_id) === String(variant.id)) + quantity > policy.stock) return 'stock';
    }
    return 'available';
  }

  renderPurchaseState(state) {
    this.purchaseLimitMessage?.classList.toggle('hidden', state !== 'limit');
    if (state === 'limit') {
      this.addButton?.remove();
      this.addButton = null;
      return;
    }
    const button = this.ensureAddButton();
    button.disabled = state !== 'available' || this.submitting;
    button.querySelector('.product-form__button-label').textContent = this.isUpcoming
      ? 'UPCOMING'
      : state === 'stock' || state === 'unavailable'
        ? window.variantStrings.soldOut
        : window.variantStrings.addToCart;
  }

  async fetchCart() {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js', { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error(this.policy.error);
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  async toggleAddButton() {
    const checkVersion = ++this.checkVersion;
    if (this.isUpcoming || !this.currentVariant?.available) {
      this.renderPurchaseState('unavailable');
      return;
    }
    if (this.addButton) this.addButton.disabled = true;
    try {
      const cart = await this.fetchCart();
      if (checkVersion !== this.checkVersion) return;
      this.renderPurchaseState(this.purchaseState(cart));
      this.retryButton.classList.add('hidden');
      if (this.cartReadFailed) {
        this.errorMessage.hidden = true;
        this.errorMessage.classList.add('hidden');
        this.cartReadFailed = false;
      }
    } catch (error) {
      if (checkVersion !== this.checkVersion) return;
      this.ensureAddButton().disabled = true;
      this.cartReadFailed = true;
      this.showError(this.policy.checkFailed);
      this.retryButton.classList.remove('hidden');
    }
  }

  showError(message) {
    this.errorMessage.textContent = message;
    this.errorMessage.hidden = false;
    this.errorMessage.classList.remove('hidden');
  }

  updateVariantInput() {
    this.variantInput.value = this.currentVariant.id;
  }

  updateImage() {
    if (!this.productImage) return;
    if (!this.currentVariant.featured_image) {
      return this.productImage.srcset = this.defaultProductImageSrc;
    }

    this.productImage.srcset = this.currentVariant.featured_image.src;
  }

  updatePrice() {
    if (!this.price) return;
    if (this.isUpcoming && this.currentVariant.price === 0) {
      this.price.textContent = 'PRICE TBA';
      return;
    }
    if (!this.currentVariant.available && !this.isUpcoming) {
      this.price.textContent = window.variantStrings.soldOut;
      return;
    }

    const formattedPrice = Shopify.formatMoney(this.currentVariant.price);
    this.price.textContent = formattedPrice;
  }

  updateVariantTitle() {
    if (!this.variantTitle) return;

    const splittedVariantTitle = this.currentVariant.title.split(' | ');
    const variantTitle = `${splittedVariantTitle[0]} | ${splittedVariantTitle[1]}`;
    this.variantTitle.textContent = variantTitle;
  }

  async onSubmitHandler(event) {
    event.preventDefault();
    if (this.isUpcoming || this.submitting || ProductForm.mutationBusy || !this.currentVariant?.available || !this.addButton || this.addButton.disabled) return;
    const submitButton = this.addButton;
    const selectedId = String(this.currentVariant.id);
    this.submitting = true;
    ProductForm.mutationBusy = true;
    ++this.checkVersion;
    submitButton.disabled = true;
    submitButton.classList.add('loading');
    this.errorMessage.hidden = true;
    this.errorMessage.classList.add('hidden');
    let redirected = false;
    let phase = 'check';
    try {
      const body = JSON.parse(serializeForm(this.form));
      const quantity = Number(body.quantity || 1);
      const cart = await this.fetchCart();
      if (String(this.currentVariant?.id) !== selectedId) return;
      const state = this.purchaseState(cart, quantity);
      if (state !== 'available') {
        this.renderPurchaseState(state);
        return;
      }
      phase = 'add';
      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig('javascript'),
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) throw Object.assign(new Error(), { shopifyMessage: result.description || result.message || this.policy.error });
      redirected = true;
      window.location.href = Shopify.locale === 'ja' ? '/cart' : `/${Shopify.locale}/cart`;
    } catch (error) {
      this.showError(error.shopifyMessage || (phase === 'check' ? this.policy.checkFailed : this.policy.error));
    } finally {
      this.submitting = false;
      submitButton.classList.remove('loading');
      if (!redirected) {
        ProductForm.mutationBusy = false;
        await this.toggleAddButton();
      }
    }
  }

};

customElements.define('product-form', ProductForm);

window.addEventListener('pageshow', event => {
  if (!event.persisted) return;
  ProductForm.mutationBusy = false;
  document.querySelectorAll('product-form').forEach(form => {
    form.submitting = false;
    form.addButton?.classList.remove('loading');
    if (form.variantRadios) {
      form.updateOptions();
      form.updateMasterId();
      if (form.currentVariant) form.updateVariantInput();
    }
    form.toggleAddButton();
  });
});
